import {
	Box,
	Button,
	Card,
	List,
	ListItem,
	ListItemText,
	ListSubheader,
	Stack,
	useTheme,
} from '@mui/material';
import React, {useEffect} from 'react';
import {useRecoilValueLoadable} from 'recoil';
import {useRecoilQuery} from 'recoil-toolkit';

import {HttpStatusCode} from 'data/protocols/http';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {DateWithWeekday, HighlightedText} from 'presentation/components';
import {useCurrentValueInputTypingStore} from 'presentation/components/Filter/store/useCurrentValueInputTypingStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {useQueryParams} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {formatText, getPageHeaderTitle} from 'presentation/utils';
import {DisabilityEnum, FlowEnum, PATH_TITLE, ShiftTypeEnum} from '../../constants';
import {useQueryParamsSync} from '../../hooks/useQueryParamsSync';
import {useSearchBar} from '../../hooks/useSearchBar/useSearchBar';
import {bookingAvailableTimesState} from '../../pages/ProfessionalSelection/atoms';

import getFormattedHour from '../../utils/getFormattedHour';
import {BookingEmptyState} from '../BookingEmptyState';
import {BookingErrorState} from '../BookingErrorState';
import {BookingLoadingState} from '../BookingLoadingState';
import {StyledStack} from './styles';
import {SelectedDateProfessionalGridProps} from './types';
import getFilteredOptions from 'presentation/modules/booking/shared/utils/getFilteredOptions';
import {textHandle} from 'presentation/utils/textHandle';
import {FavoriteDoctorsLikeButton} from 'presentation/modules/booking/favoriteDoctors/components/FavoriteDoctorsLikeButton';
import {FeaturesEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {PreviousProviderCard} from 'presentation/modules/booking/shared/ui/components/ProfessionalSelection/PreviousProviderCard';

const {booking} = translations['pt-br'].others.sidebar;
const {loadingError, loadingState, emptySearchState} =
	translations['pt-br'].privateFlow.bookingFlow.professionalFlow;

export function SelectedDateProfessionalGrid({
	flowType,
	handleNext,
	handlePrev,
}: SelectedDateProfessionalGridProps) {
	const bookingData = useBookingStore(state => state.bookingData);
	const {activeBookingFlowStep} = useQueryParamsSync();
	const currentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.currentValueInputTyping,
	);
	const setCurrentValueInputTypingState = useCurrentValueInputTypingStore(
		state => state.setCurrentValueInputTyping,
	);
	const isOdontoFlow = flowType === FlowEnum.ODONTO;

	const {SEARCH_PARAMS} = useQueryParams();
	const {colors} = useTheme();

	const {shift, disability, query, autocompleteQuery} = SEARCH_PARAMS;

	const {handleClearShiftAndSearch, getProviderNamesState} = useSearchBar({
		activeStep: activeBookingFlowStep,
		flowType,
	});

	const normalizedQuery = textHandle(query).sanitizeText();
	const queryToFilter = normalizedQuery === 'clear' ? '' : normalizedQuery;

	const availableTimesParams = {
		cityName: bookingData.cityName,
		dateAt: bookingData.dateAt,
		disability: disability as DisabilityEnum,
		districtName: isOdontoFlow
			? bookingData.odontoUnitDistrictSelected
			: bookingData.districtName,
		flowType,
		unitCode: bookingData.unitCode,
		shift: shift as ShiftTypeEnum,
		specialtyCode: bookingData.specialtyCode,
		ufCode: bookingData.ufCode,
		subSpecialtyCode: bookingData.subSpecialtyCode,
		providerId:
			(bookingData?.isFavoriteDoctorsFlow && bookingData?.professionalCode) ||
			autocompleteQuery,
	};

	const pageTitle = getPageHeaderTitle({
		internalPaths: PATH_TITLE,
		mainPath: booking,
	});

	const availableTimesList = useRecoilValueLoadable(
		bookingAvailableTimesState(availableTimesParams),
	);

	const {refresh} = useRecoilQuery(bookingAvailableTimesState(availableTimesParams));

	const options =
		getProviderNamesState?.medicos?.map(({cdPrestadorFisico, nmMedico}) => ({
			id: cdPrestadorFisico,
			label: nmMedico,
		})) || [];

	const FILTERED_OPTIONS = getFilteredOptions(options, currentValueInputTypingState);

	const hasFilteredOptions = FILTERED_OPTIONS.length > 0;

	useEffect(() => {
		refresh();
	}, []);

	const {isEnabled: isPreviousProviderEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_BOOKING_PREVIOUS_PROVIDER,
	);

	if (availableTimesList.state === 'hasError') {
		const statusCode = availableTimesList?.contents?.statusCode;

		if (statusCode === HttpStatusCode.unprocessable) {
			return (
				<BookingEmptyState
					title={emptySearchState.emptyTitle}
					description={emptySearchState.emptyDescription}
					flowType={flowType}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROFISSIONAL_ENCONTRADO}
					clearFilter={handleClearShiftAndSearch}
				/>
			);
		}
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={pageTitle}
				errorTitle={loadingError}
				handleBack={handlePrev}
				handleRefresh={refresh}
			/>
		);
	}

	if (availableTimesList.state === 'loading') {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={pageTitle}
				loadingText={loadingState}
				handleBack={handlePrev}
			/>
		);
	}

	const FILTERED_LIST_BY_PROFESSIONAL = !autocompleteQuery
		? availableTimesList.contents.filter(({professionalName}) =>
				textHandle(professionalName).sanitizeText()?.includes(queryToFilter),
			)
		: availableTimesList.contents;

	const shouldShowBookingEmptyState =
		FILTERED_LIST_BY_PROFESSIONAL.length === 0 ||
		(!hasFilteredOptions && currentValueInputTypingState);

	if (shouldShowBookingEmptyState) {
		return (
			<Box>
				<BookingEmptyState
					title={emptySearchState.emptyTitle}
					description={emptySearchState.emptyDescription}
					flowType={flowType}
					dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROFISSIONAL_ENCONTRADO}
					clearFilter={() => {
						handleClearShiftAndSearch();
						setCurrentValueInputTypingState('');
					}}
				/>
			</Box>
		);
	}

	const isHealthOrTelehealth = flowType === FlowEnum.HEALTH || flowType === FlowEnum.TELEHEALTH;
	const shouldSkipPreviousProvider =
		!isPreviousProviderEnabled || Boolean(autocompleteQuery) || !isHealthOrTelehealth;

	const previousProviderData = (() => {
		if (shouldSkipPreviousProvider) return null;

		const provider = FILTERED_LIST_BY_PROFESSIONAL?.find(t => t.isPreviousProvider);
		if (!provider || !provider.availableTimes.length) return null;

		return {
			professionalName: provider.professionalName,
			professionalCode: provider.professionalCode,
			recommendations: provider.recommendations,
			isFavorite: provider.isFavorite,
			dates: [{date: bookingData.dateAt ?? '', availableTimes: provider.availableTimes}],
		};
	})();

	const timesWithoutPreviousProvider = shouldSkipPreviousProvider
		? FILTERED_LIST_BY_PROFESSIONAL
		: FILTERED_LIST_BY_PROFESSIONAL.filter(t => !t.isPreviousProvider);

	const showPreviousProvider = !shouldSkipPreviousProvider && previousProviderData !== null;

	return (
		<List
			sx={{
				width: '100%',
				position: 'relative',
				overflow: 'auto',
			}}
			subheader={<li />}>
			{showPreviousProvider && previousProviderData ? (
				<PreviousProviderCard
					flowType={flowType}
					professionalCode={previousProviderData.professionalCode}
					professionalName={previousProviderData.professionalName}
					dates={previousProviderData.dates}
					recommendations={previousProviderData.recommendations}
					onSelectHour={handleNext}
					isFavorite={previousProviderData.isFavorite}
				/>
			) : null}
			{timesWithoutPreviousProvider.length > 0 ? (
				<li>
					<ul>
						<ListSubheader
							sx={{
								marginTop: '16px',
								padding: 0,
								bgcolor: 'transparent',
								color: colors.light.text.primary,
							}}>
							<DateWithWeekday date={bookingData.dateAt} />
						</ListSubheader>
						{timesWithoutPreviousProvider.map(item => (
							<ListItem key={item.professionalCode} disableGutters>
								<Card elevation={0} sx={{p: 2, width: '100%'}}>
									<Stack
										flexDirection="row"
										alignItems="center"
										justifyContent="space-between">
										<ListItemText
											primary={
												autocompleteQuery || hasFilteredOptions ? (
													formatText(item.professionalName)
												) : (
													<HighlightedText
														highlight={query}
														text={formatText(item.professionalName)}
													/>
												)
											}
											slotProps={{primary: {variant: 'h6'}}}
										/>
										{flowType === FlowEnum.HEALTH ||
										flowType === FlowEnum.TELEHEALTH ? (
											<FavoriteDoctorsLikeButton
												cdPrestador={Number(item.professionalCode)}
												cdEspecialidade={Number(bookingData.specialtyCode)}
												cdSubEspecialidade={Number(
													bookingData.subSpecialtyCode,
												)}
												selected={item.isFavorite}
											/>
										) : null}
									</Stack>
									<StyledStack
										direction="row"
										gap={1}
										overflow="auto"
										mt={2}
										pb={2}>
										{item.availableTimes.map((hour, index) => (
											<Button
												onClick={() =>
													handleNext({
														hour,
														profCode: item.professionalCode,
														profName: item.professionalName,
														date: bookingData.dateAt,
														recommendations: item.recommendations,
													})
												}
												key={`${hour}-${index}`}
												variant={
													bookingData.hourAt === hour &&
													bookingData.professionalCode ===
														item.professionalCode
														? 'contained'
														: 'outlined'
												}
												sx={{
													borderRadius: 0.5,
													fontSize: '1rem',
												}}>
												{getFormattedHour(hour, flowType)}
											</Button>
										))}
									</StyledStack>
								</Card>
							</ListItem>
						))}
					</ul>
				</li>
			) : null}
		</List>
	);
}
